import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, RefreshCcw, Rocket } from "lucide-react";

/**
 * FlexibilityInitiative — subscription-overview card shown above the tier
 * cards on the pricing / subscribe page. Bullets:
 *   · Risk-Free Trial
 *   · 50% Activation Rebate
 *   · 30-Day Integrity Guarantee
 * Followed by the required "I agree to the Terms & Conditions" affordance.
 */
export default function FlexibilityInitiative({
  showAgree = true,
  agreed = false,
  onAgreeChange,
}) {
  return (
    <section
      data-testid="flexibility-initiative"
      className="max-w-4xl mx-auto border border-emerald-500/50 bg-emerald-500/5 p-5 mb-6"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={12} className="text-emerald-300" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-emerald-300">
          SUBSCRIPTION_OVERVIEW · THE_FLEXIBILITY_INITIATIVE
        </span>
      </div>
      <h3 className="font-mono text-xl sm:text-2xl tracking-tight text-white">
        The Flexibility Initiative
      </h3>
      <ul className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <li className="border border-emerald-500/30 bg-[#0A0A0A]/60 p-3">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
            <Rocket size={11} /> RISK_FREE_TRIAL
          </div>
          <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
            <b className="text-white">Risk-Free Trial:</b> Explore the
            platform&apos;s full capabilities completely free — a{" "}
            <b>$0.01 verification</b> is placed on your card for honesty and{" "}
            <b>auto-refunded</b> the moment your account is verified.
          </p>
        </li>
        <li className="border border-emerald-500/30 bg-[#0A0A0A]/60 p-3">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-amber-300">
            <RefreshCcw size={11} /> 50%_ACTIVATION_REBATE
          </div>
          <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
            <b className="text-white">50% Activation Rebate:</b> Get a{" "}
            <b>50% credit</b> back on your first month (valid for up to{" "}
            <b>three bot deployments</b>) automatically upon subscription.
          </p>
        </li>
        <li className="border border-emerald-500/30 bg-[#0A0A0A]/60 p-3">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-purple-300">
            <ShieldCheck size={11} /> 30_DAY_INTEGRITY_GUARANTEE
          </div>
          <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
            <b className="text-white">30-Day Integrity Guarantee:</b> Annual
            plans can cancel within the first 30 days. You will only be billed
            for a single month at the standard rate, and the rest is fully
            refunded. <b className="text-emerald-300">First-renewal bonus:</b>{" "}
            annual and Team plans receive <b>+100 free MindStein usages</b> on the
            first successful renewal (one-time).
          </p>
        </li>
      </ul>
      {showAgree && (
        <label
          data-testid="flexibility-agree-row"
          className="mt-4 flex items-start gap-2 text-[12px] font-mono text-zinc-300 cursor-pointer"
        >
          <input
            type="checkbox"
            data-testid="flexibility-agree-checkbox"
            checked={!!agreed}
            onChange={(e) => onAgreeChange?.(e.target.checked)}
            className="mt-1 h-4 w-4 accent-emerald-400 flex-shrink-0"
          />
          <span>
            <b>[ I agree to the Terms &amp; Conditions ]</b>{" "}
            — read the{" "}
            <Link to="/legal" target="_blank" rel="noopener noreferrer"
                  className="text-amber-300 hover:text-white underline">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer"
                  className="text-emerald-300 hover:text-white underline">
              Privacy Policy
            </Link>. This includes the Flexibility Subscription Terms &amp;
            Rebate Policy in section 12.
          </span>
        </label>
      )}
    </section>
  );
}
